import { Injectable, OnDestroy } from '@angular/core';
import { Html5Qrcode, Html5QrcodeResult, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Html5QrcodeError, QrDimensions } from 'html5-qrcode/esm/core';
import { Observable, Subject } from 'rxjs';
import { DialogService } from './dialog.service';

export enum AppScanEventType {
    Initialized = 1,
    Scanning = 2,
    Success = 3,
}

export interface AppScanEvent {
    type: AppScanEventType;
    value: string | null;
    message?: string;
}

@Injectable()
export class ScanCameraService implements OnDestroy {
    private _scanner: Html5Qrcode | null = null;
    private _events: Subject<AppScanEvent> = new Subject<AppScanEvent>();

    constructor(private readonly dialogService: DialogService) {}

    get emitter$(): Observable<AppScanEvent> {
        return this._events;
    }

    ngOnDestroy(): void {
        if (this._scanner && this._scanner.getState() !== Html5QrcodeScannerState.NOT_STARTED) {
            this._scanner.stop();
        }
    }

    /**
     *  @param elementId ID Элемента отрисовки
     *  @param formats Список форматов для реагирования считывателя (DEFAULT: Html5QrcodeSupportedFormats.QR_CODE)
     */
    initialize(
        elementId: string,
        cameraId: string,
        formats: Array<Html5QrcodeSupportedFormats> = [Html5QrcodeSupportedFormats.QR_CODE]
    ): void {
        this._scanner = new Html5Qrcode(elementId, {
            formatsToSupport: formats,
            verbose: true,
        });
        this.runScanner(cameraId);
    }

    pause(pauseVideo: boolean = false): void {
        this._scanner?.pause(pauseVideo);
    }

    stop(): void {
        this._scanner?.stop();
    }

    resume(): void {
        this._scanner?.resume();
    }

    runScanner(id: string): void {
        this._scanner
            ?.start(id, { fps: 30, qrbox: this.calculateQrboxSize }, this.onScanSuccess.bind(this), this.onScanError.bind(this))
            .then(() => {
                this._events.next({ type: AppScanEventType.Initialized, value: null, message: 'Сканер начал работу' });
            })
            .catch(({ message }) => {
                this.dialogService.notifyInSnackBar(`Не удалось сканирование: (ERROR_TEXT: ${message})`, 'error');
            });
    }

    private onScanSuccess(decodedText: string, result: Html5QrcodeResult): void {
        this._events.next({ type: AppScanEventType.Success, value: decodedText, message: 'Значение получено' });
    }

    private onScanError(errorMessage: string, error: Html5QrcodeError): void {
        this._events.next({ type: AppScanEventType.Scanning, value: null, message: errorMessage });
    }

    private calculateQrboxSize(viewfinderWidth: number, viewfinderHeight: number): QrDimensions {
        const minEdgePercentage = 0.8;
        const minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
        const qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
        return {
            width: qrboxSize,
            height: qrboxSize,
        };
    }
}
